// =============================================================
// VARIANCE — The Executive Command Center
// File      : SalesLaunchpad.tsx
// Location  : /home/Applications/variance/web/src/pages/SalesLaunchpad.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : The hub page for Marketing, Pipeline, Bookings & CRM.
// =============================================================

import { useNavigate } from 'react-router-dom';
import { Megaphone, TrendingUp, IndianRupee, Building2 } from 'lucide-react';
import { PageHelp } from '../components/HelpSystem';

export function SalesLaunchpad() {
  const navigate = useNavigate();

  return (
    <div className="max-w-6xl mx-auto mt-12 mb-20">
      
        <PageHelp 
        title="Sales Hub Overview" 
        description="Welcome to the Revenue Engine. Use Marketing to generate leads. Move qualified leads into the Sales Pipeline. Once a deal is won, Book the unit here and track Collections. Finally, manage the customer relationship in Post-Sales & CRM."
      />

      {/* Definition Header */}
      <div className="text-center mb-16 space-y-6">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Sales Hub</h1>
        <div className="w-full flex justify-center">
          <p className="text-base md:text-lg leading-relaxed text-justify md:text-center max-w-4xl px-4" style={{ color: 'var(--text-secondary)' }}>
            The complete revenue engine of the enterprise. Manage 
            <span className="font-semibold" style={{ color: 'var(--brand-accent)' }}> Marketing</span> (Pre-Sales), 
            <span className="font-semibold" style={{ color: 'var(--brand-accent)' }}> Sales</span> (Deals & Pipelines), 
            <span className="font-semibold" style={{ color: 'var(--brand-accent)' }}> Bookings & Inventory</span>, and 
            <span className="font-semibold" style={{ color: 'var(--brand-accent)' }}> CRM</span> (Post-Sales & Collections).
          </p>
        </div>
      </div>

      {/* Launchpad Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        
        {/* 1. Marketing Card */}
        <button 
          onClick={() => navigate('/sales/marketing')} 
          className="group relative p-8 rounded-2xl flex flex-col items-center gap-6 transition-all duration-300 border-2 border-[var(--border-subtle)] hover:border-[var(--brand-accent)] hover:shadow-[0_0_30px_var(--brand-accent)]"
          style={{ backgroundColor: 'var(--bg-panel)' }}
        >
          <div className="p-5 rounded-full transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
            <Megaphone size={48} style={{ color: 'var(--brand-accent)' }} />
          </div>
          <div className="text-center">
            <span className="block font-bold text-xl mb-2" style={{ color: 'var(--text-primary)' }}>Marketing</span>
            <span className="block text-sm" style={{ color: 'var(--text-secondary)' }}>Campaigns & Lead Gen</span>
          </div>
        </button>

        {/* 2. Sales Pipeline Card */}
        <button 
          onClick={() => navigate('/sales/pipeline')} 
          className="group relative p-8 rounded-2xl flex flex-col items-center gap-6 transition-all duration-300 border-2 border-[var(--border-subtle)] hover:border-[var(--brand-accent)] hover:shadow-[0_0_30px_var(--brand-accent)]"
          style={{ backgroundColor: 'var(--bg-panel)' }}
        >
          <div className="p-5 rounded-full transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
            <TrendingUp size={48} style={{ color: 'var(--brand-accent)' }} />
          </div>
          <div className="text-center">
            <span className="block font-bold text-xl mb-2" style={{ color: 'var(--text-primary)' }}>Sales Pipeline</span>
            <span className="block text-sm" style={{ color: 'var(--text-secondary)' }}>Leads & Deals Kanban</span>
          </div>
        </button>

        {/* 3. Inventory & Bookings Card (NEW) */}
        <button 
          onClick={() => navigate('/sales/bookings')} 
          className="group relative p-8 rounded-2xl flex flex-col items-center gap-6 transition-all duration-300 border-2 border-[var(--border-subtle)] hover:border-[var(--brand-accent)] hover:shadow-[0_0_30px_var(--brand-accent)]"
          style={{ backgroundColor: 'var(--bg-panel)' }}
        >
          <div className="p-5 rounded-full transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
            <Building2 size={48} style={{ color: 'var(--brand-accent)' }} />
          </div>
          <div className="text-center">
            <span className="block font-bold text-xl mb-2" style={{ color: 'var(--text-primary)' }}>Sales Inventory</span>
            <span className="block text-sm" style={{ color: 'var(--text-secondary)' }}>Units, Bookings & Collections</span>
          </div>
        </button>

        {/* 4. CRM & Collections Card */}
        <button 
          onClick={() => navigate('/sales/crm')} 
          className="group relative p-8 rounded-2xl flex flex-col items-center gap-6 transition-all duration-300 border-2 border-[var(--border-subtle)] hover:border-[var(--brand-accent)] hover:shadow-[0_0_30px_var(--brand-accent)]"
          style={{ backgroundColor: 'var(--bg-panel)' }}
        >
          <div className="p-5 rounded-full transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
            <IndianRupee size={48} style={{ color: 'var(--brand-accent)' }} />
          </div>
          <div className="text-center">
            <span className="block font-bold text-xl mb-2" style={{ color: 'var(--text-primary)' }}>Post-Sales & CRM</span>
            <span className="block text-sm" style={{ color: 'var(--text-secondary)' }}>Customer Care & Handover</span>
          </div>
        </button>

      </div>
    </div>
  );
}
